// src/pages/Reports/AgentDebtReport/AgentDebtDetailModal.tsx
// dac-ta-bao-cao-cong-no-dai-ly.md §20 — chi tiết công nợ 1 đại lý: (1) đầu kỳ, (2) phát sinh trong
// kỳ, (3) dòng tiền trong kỳ, (4) dòng tiền lũy kế, (5) công nợ theo từng kỳ, (6) lịch sử giao dịch
// (sổ công nợ §21 + sổ dòng tiền §22).

import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Nav, NavItem, NavLink, Spinner, TabContent, TabPane } from 'reactstrap';

import { AgentMap, getAgentCode, getAgentName } from 'helpers/agentLookup';
import { getAgentDebtDetailApi } from 'helpers/fakebackend_helper';
import { formatCurrency, formatDate, formatDateTime } from 'pages/Reports/IssuanceReporting/shared/formatters';
import { AgentDebtDetail } from '../types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('reports_agent_debt_report');

interface Props {
  isOpen: boolean;
  agentId: number | null;
  agentMap: AgentMap;
  fromDate?: string;
  toDate?: string;
  planId?: number;
  onExport: (format: 'Xlsx' | 'Csv') => void;
  onClose: () => void;
}

const Line: React.FC<{ label: string; value: number; sign?: '+' | '-'; strong?: boolean }> = ({ label, value, sign, strong }) => (
  <tr>
    <td className="bg-light fw-semibold" style={{ width: '55%' }}>{label}</td>
    <td className={`text-end ${strong ? 'fw-bold' : ''} ${sign === '-' ? 'text-danger' : sign === '+' ? 'text-success' : ''}`}>
      {sign ?? ''}{formatCurrency(value)}
    </td>
  </tr>
);

const AgentDebtDetailModal: React.FC<Props> = ({ isOpen, agentId, agentMap, fromDate, toDate, planId, onExport, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<AgentDebtDetail | null>(null);
  const [tab, setTab] = useState<'summary' | 'ledger' | 'cashflow' | 'periods'>('summary');

  useEffect(() => {
    if (!isOpen || !agentId) return;
    setLoading(true);
    setTab('summary');
    getAgentDebtDetailApi(agentId, {
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      planId: planId || undefined,
      transactionLimit: 200,
    })
      .then((res) => setData((res as any)?.data ?? res))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [isOpen, agentId, fromDate, toDate, planId]);

  const title = useMemo(
    () => (agentId ? `${getAgentName(agentMap, agentId)} (${getAgentCode(agentMap, agentId)})` : ''),
    [agentId, agentMap],
  );

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="xl" scrollable>
      <ModalHeader toggle={onClose}>
        {st('chi_tiet_cong_no_title')} {title}
      </ModalHeader>
      <ModalBody>
        {loading || !data ? (
          <div className="text-center py-5"><Spinner color="primary" /></div>
        ) : (
          <>
            <Nav tabs className="mb-3">
              <NavItem><NavLink role="button" active={tab === 'summary'} onClick={() => setTab('summary')}>{st('tong_hop_text')}</NavLink></NavItem>
              <NavItem><NavLink role="button" active={tab === 'ledger'} onClick={() => setTab('ledger')}>{st('so_cong_no_text')}{data.transactions.length})</NavLink></NavItem>
              <NavItem><NavLink role="button" active={tab === 'cashflow'} onClick={() => setTab('cashflow')}>{st('so_dong_tien_text')}{data.cashFlowTransactions.length})</NavLink></NavItem>
              <NavItem><NavLink role="button" active={tab === 'periods'} onClick={() => setTab('periods')}>{st('theo_ky_text')}{data.byPeriod.length})</NavLink></NavItem>
            </Nav>

            <TabContent activeTab={tab}>
              {/* Mục 1-4 */}
              <TabPane tabId="summary">
                <h6 className="mb-2">{st('1_2_cong_no_dau_ky_title')}</h6>
                <div className="table-responsive mb-4">
                  <table className="table table-sm table-bordered mb-0">
                    <tbody>
                      <Line label={st('cong_no_dau_ky_label')} value={data.openingBalance} />
                      <Line label={st('ve_ban_label')} value={data.ticketSaleValue} sign="+" />
                      <Line label={st('ve_thu_hoi_label')} value={data.ticketReturnValue} sign="-" />
                      <Line label={st('hoa_hong_ban_ve_label')} value={data.salesCommission} sign="-" />
                      <Line label={st('hoa_hong_tra_thuong_label')} value={data.prizePayoutCommission} sign="-" />
                      <Line label={st('dieu_chinh_tang_label')} value={data.adjustmentIncrease} sign="+" />
                      <Line label={st('dieu_chinh_giam_label')} value={data.adjustmentDecrease} sign="-" />
                      <Line label={st('da_thanh_toan_label')} value={data.paidAmount} sign="-" />
                      <Line label={st('cong_no_cuoi_ky_label')} value={data.closingBalance} strong />
                    </tbody>
                  </table>
                </div>

                <h6 className="mb-2">{st('3_4_dong_tien_title')}</h6>
                <div className="table-responsive">
                  <table className="table table-sm table-bordered mb-0">
                    <thead><tr><th></th><th className="text-end">{st('trong_ky_column')}</th><th className="text-end">{st('luy_ke_column')}</th></tr></thead>
                    <tbody>
                      <tr><td className="bg-light fw-semibold">{st('tien_vao_text')}</td><td className="text-end text-success">{formatCurrency(data.cashIn)}</td><td className="text-end text-success">{formatCurrency(data.cashInCumulative)}</td></tr>
                      <tr><td className="bg-light fw-semibold">{st('tien_ra_text')}</td><td className="text-end text-danger">{formatCurrency(data.cashOut)}</td><td className="text-end text-danger">{formatCurrency(data.cashOutCumulative)}</td></tr>
                      <tr><td className="bg-light fw-semibold">{st('dong_tien_thuan_text')}</td><td className="text-end fw-bold">{formatCurrency(data.cashNet)}</td><td className="text-end fw-bold">{formatCurrency(data.cashNetCumulative)}</td></tr>
                    </tbody>
                  </table>
                </div>
              </TabPane>

              {/* Mục 6a — sổ công nợ §21 */}
              <TabPane tabId="ledger">
                <div className="table-responsive">
                  <table className="table table-sm table-bordered mb-0">
                    <thead>
                      <tr>
                        <th>{st('ngay_column')}</th><th>{st('ky_column')}</th><th>{st('loai_nghiep_vu_column')}</th>
                        <th className="text-end">{st('phat_sinh_tang_column')}</th><th className="text-end">{st('phat_sinh_giam_column')}</th>
                        <th className="text-end">{st('thanh_toan_column')}</th><th className="text-end">{st('so_du_column')}</th><th>{st('noi_dung_column')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.transactions.map((t) => (
                        <tr key={`${t.id}-${t.transactionType}`}>
                          <td>{formatDate(t.transactionDate)}</td>
                          <td>{t.planCode ?? '-'}</td>
                          <td>{t.transactionTypeName}</td>
                          <td className="text-end">{t.debitAmount ? formatCurrency(t.debitAmount) : ''}</td>
                          <td className="text-end">{t.creditAmount ? formatCurrency(t.creditAmount) : ''}</td>
                          <td className="text-end">{t.paymentAmount ? formatCurrency(t.paymentAmount) : ''}</td>
                          <td className="text-end fw-semibold">{formatCurrency(t.balance)}</td>
                          <td className="text-muted" style={{ fontSize: 12 }}>{t.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </TabPane>

              {/* Mục 6b — sổ dòng tiền §22 */}
              <TabPane tabId="cashflow">
                <div className="table-responsive">
                  <table className="table table-sm table-bordered mb-0">
                    <thead>
                      <tr>
                        <th>{st('ngay_column')}</th><th>{st('ky_column')}</th><th>{st('loai_giao_dich_column')}</th>
                        <th className="text-end">{st('tien_vao_text')}</th><th className="text-end">{st('tien_ra_text')}</th>
                        <th className="text-end">{st('thuan_column')}</th><th className="text-end">{st('so_du_luy_ke_column')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.cashFlowTransactions.length === 0 ? (
                        <tr><td colSpan={7} className="text-center text-muted py-3">{st('chua_co_giao_dich_tien_text')}</td></tr>
                      ) : data.cashFlowTransactions.map((c) => (
                        <tr key={c.id}>
                          <td>{formatDate(c.transactionDate)}</td>
                          <td>{c.planCode ?? '-'}</td>
                          <td>{c.transactionTypeName}</td>
                          <td className="text-end text-success">{c.cashIn ? formatCurrency(c.cashIn) : ''}</td>
                          <td className="text-end text-danger">{c.cashOut ? formatCurrency(c.cashOut) : ''}</td>
                          <td className="text-end">{formatCurrency(c.cashNet)}</td>
                          <td className="text-end fw-semibold">{formatCurrency(c.runningBalance)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </TabPane>

              {/* Mục 5 — công nợ theo từng kỳ §16 */}
              <TabPane tabId="periods">
                <div className="table-responsive">
                  <table className="table table-sm table-bordered mb-0">
                    <thead>
                      <tr>
                        <th>{st('ky_column')}</th><th>{st('ngay_xo_column')}</th>
                        <th className="text-end">{st('ve_ban_label')}</th><th className="text-end">{st('thu_hoi_column')}</th>
                        <th className="text-end">{st('hh_ban_column')}</th><th className="text-end">{st('hh_thuong_column')}</th>
                        <th className="text-end">{st('da_thanh_toan_label')}</th><th className="text-end">{st('cong_no_cuoi_ky_column')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.byPeriod.map((p, i) => (
                        <tr key={p.issuancePlanId ?? `null-${i}`}>
                          <td>{p.planCode ?? st('khong_gan_ky_text')}</td>
                          <td>{formatDate(p.expectedDrawDate)}</td>
                          <td className="text-end">{formatCurrency(p.ticketSaleValue)}</td>
                          <td className="text-end">{formatCurrency(p.ticketReturnValue)}</td>
                          <td className="text-end">{formatCurrency(p.salesCommission)}</td>
                          <td className="text-end">{formatCurrency(p.prizePayoutCommission)}</td>
                          <td className="text-end text-success">{formatCurrency(p.paidAmount)}</td>
                          <td className="text-end fw-bold">{formatCurrency(p.closingBalance)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </TabPane>
            </TabContent>

            <p className="text-muted mt-2 mb-0" style={{ fontSize: 12 }}>
              {st('khoang_loc_text')} {fromDate ? formatDate(fromDate) : st('tu_dau_text')} → {toDate ? formatDate(toDate) : formatDateTime(new Date().toISOString())}
            </p>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="success" outline size="sm" onClick={() => onExport('Xlsx')}>
          <i className="ri-file-excel-2-line align-bottom me-1" />{st('xuat_chi_tiet_button')}
        </Button>
        <Button color="secondary" onClick={onClose}>{st('dong_button')}</Button>
      </ModalFooter>
    </Modal>
  );
};

export default AgentDebtDetailModal;
